import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useUpdateUserSettings } from '../../hooks/useUpdateUserSettings';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { Box } from '@televet/kibble-ui/build/chakra';
import { DatePicker, DateRange } from '@televet/kibble-ui/build/components/DatePicker';
import { TimePicker } from '@televet/kibble-ui/build/components/TimePicker';
import { useTracking } from 'shared/providers/TrackingProvider';
import { CustomSnoozeModalEvents } from 'shared/providers/TrackingProvider/EventNames';

type CustomSnoozeInput = {
  date: Date | null;
  time: Date;
};

interface CustomSnoozeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CustomSnoozeModal = ({ isOpen, onClose }: CustomSnoozeModalProps): JSX.Element | null => {
  const toast = useToast();
  const { trackEvent } = useTracking();
  const { update, loading } = useUpdateUserSettings();
  const { handleSubmit, control, errors, watch, setValue, triggerValidation } = useForm<CustomSnoozeInput>({
    validateCriteriaMode: 'all',
  });

  const saveCustomSnooze = async (values: CustomSnoozeInput): Promise<void> => {
    const { date, time } = values;
    if (!date || !time) {
      return;
    }

    date.setHours(time.getHours());
    date.setMinutes(time.getMinutes());

    if (date <= new Date()) {
      toast({
        status: 'error',
        title: 'Please select a future date and time',
      });
      return;
    }

    try {
      await update({ snoozeNotificationsUntil: date.toISOString() });
      toast({
        status: 'success',
        title: 'Notifications paused successfully',
      });
      trackEvent(CustomSnoozeModalEvents.SnoozeSettingsSaved);
    } catch (error) {
      toast({
        status: 'error',
        title: 'Failed to pause notifications, please try again.',
      });
    } finally {
      onClose();
    }
  };

  const watchStartDate = watch('date');
  const watchStartTime = watch('time');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <Box as="form">
        <ModalHeader>Pause Notifications Until...</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box className="SnoozeDate">
            <Controller
              as={DatePicker}
              name="date"
              control={control}
              startDate={watchStartDate}
              minDate={new Date()}
              dateFormat="MM/dd/yyyy"
              rules={{ required: true }}
              inputProps={{
                isInvalid: !!errors.date,
                errorText: 'Please enter a valid date (mm/dd/yyy)',
                labelStyle: { fontWeight: 'semibold' },
                label: 'Date',
                isRequired: true,
              }}
              onDateChange={(date: DateRange): void => {
                setValue('date', date.startDate);
                triggerValidation('date');
              }}
            />
          </Box>
          <Box className="SnoozeTime" pt={4}>
            <Controller
              as={TimePicker}
              name="time"
              control={control}
              initialTime={watchStartTime}
              minuteInterval={5}
              rules={{ required: true }}
              inputProps={{
                isInvalid: !!errors.time,
                errorText: 'This field is required',
                labelStyle: { fontWeight: 'semibold' },
                label: 'Time',
                isRequired: true,
              }}
              onTimeChange={(time: Date): void => {
                setValue('time', time);
                triggerValidation('time');
              }}
            />
          </Box>
        </ModalBody>
        <ModalFooterButtons
          onCancel={(): void => trackEvent(CustomSnoozeModalEvents.SnoozeSettingsCancelled)}
          submitText="Pause Notifications"
          onSubmit={handleSubmit(saveCustomSnooze)}
          isLoading={loading}
        />
      </Box>
    </Modal>
  );
};
